import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  Calendar, 
  Clock,
  Dumbbell,
  CheckCircle,
  Circle,
  ArrowLeft,
  Play,
  TrendingUp
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { WorkoutService } from '../lib/workoutService';
import { MemberWorkoutPlan, WorkoutPlanWithSchedule } from '../types/dataModel';
import { LoadingSpinner } from '../components/LoadingSpinner';
import { PAGE_CHROME_LOADER_BRAND_GREEN, PAGE_CHROME_OFF_WHITE } from '../constants/pageChrome';
import { usePageStatusBar } from '../hooks/usePageStatusBar';

export const ActiveWorkoutPlans = () => {
  const { profile, user } = useAuth();
  const [plans, setPlans] = useState<(MemberWorkoutPlan & { workoutPlan: WorkoutPlanWithSchedule })[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  usePageStatusBar({
    background: loading ? PAGE_CHROME_LOADER_BRAND_GREEN : PAGE_CHROME_OFF_WHITE,
  });

  useEffect(() => {
    const fetchPlans = async () => {
      if (!user) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        const memberId = profile?.id || user.id;
        const fetchedPlans = await WorkoutService.getMemberWorkoutPlansWithDetails(memberId);
        setPlans(fetchedPlans);
      } catch (err: any) {
        console.error('Error fetching workout plans:', err);
        setError(err.message || 'Failed to load workout plans');
      } finally {
        setLoading(false);
      }
    };

    fetchPlans();
  }, [user, profile]);

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric',
      year: 'numeric'
    });
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'active':
        return (
          <span className="px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-800">
            Active
          </span>
        );
      case 'completed':
        return (
          <span className="px-2 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-800">
            Completed
          </span>
        );
      case 'paused':
        return (
          <span className="px-2 py-1 text-xs font-medium rounded-full bg-yellow-100 text-yellow-800">
            Paused
          </span>
        );
      default:
        return (
          <span className="px-2 py-1 text-xs font-medium rounded-full bg-gray-100 text-gray-800">
            {status}
          </span>
        );
    }
  };

  if (loading) {
    return <LoadingSpinner fullScreen />;
  }

  return (
    <div className="min-h-screen bg-cream-50 pb-bottom-nav">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-6">
          <Link
            to="/member-dashboard"
            className="inline-flex items-center text-sm text-gray-600 hover:text-primary-600 mb-4"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Dashboard
          </Link>
          <h1 className="text-3xl font-heading font-bold text-gray-900">
            Your Workout Plans
          </h1>
          <p className="text-gray-600 mt-2">
            Manage and track your active workout programs
          </p>
        </div>

        {/* Error Message */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-sm text-red-700">{error}</p>
          </div>
        )}

        {/* Workout Plans List */}
        {plans.length === 0 ? (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
            <Dumbbell className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-900 mb-2">
              No Workout Plans Yet
            </h3>
            <p className="text-gray-600 mb-6">
              You don't have any workout plans assigned. Browse available plans to get started!
            </p>
            <Link
              to="/workout-plans"
              className="inline-flex items-center px-6 py-3 bg-[var(--action-primary)] text-[var(--ink)] rounded-lg font-medium hover:bg-[var(--action-primary-pressed)] transition-colors"
            >
              Browse Workout Plans
            </Link>
          </div>
        ) : (
          <div className="space-y-6">
            {plans.map((plan) => (
              <div
                key={plan.id}
                className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow"
              >
                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <h2 className="text-2xl font-heading font-semibold text-gray-900 mb-2">
                          {plan.workoutPlan.name}
                        </h2>
                        {plan.workoutPlan.description && (
                          <p className="text-gray-600 mb-3">
                            {plan.workoutPlan.description}
                          </p>
                        )}
                      </div>
                      {getStatusBadge(plan.status)}
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4">
                      <div className="flex items-center gap-2 text-sm text-gray-600">
                        <Calendar className="w-4 h-4 text-primary-600" />
                        <span>
                          Started: {formatDate(plan.startDate)}
                        </span>
                      </div>
                      {plan.endDate && (
                        <div className="flex items-center gap-2 text-sm text-gray-600">
                          <CheckCircle className="w-4 h-4 text-primary-600" />
                          <span>
                            Ends: {formatDate(plan.endDate)}
                          </span>
                        </div>
                      )}
                      {plan.workoutPlan.totalNoOfDays && (
                        <div className="flex items-center gap-2 text-sm text-gray-600">
                          <Clock className="w-4 h-4 text-primary-600" />
                          <span>
                            {Math.ceil(plan.workoutPlan.totalNoOfDays / 7)} weeks ({plan.workoutPlan.totalNoOfDays} days)
                          </span>
                        </div>
                      )}
                    </div>

                    {/* Workout Schedule Summary */}
                    {plan.workoutPlan.planWorkouts && plan.workoutPlan.planWorkouts.length > 0 && (
                      <div className="mt-4 pt-4 border-t border-gray-200">
                        <h3 className="text-sm font-semibold text-gray-900 mb-3">
                          Workout Schedule
                        </h3>
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                          {plan.workoutPlan.planWorkouts.slice(0, 6).map((planWorkout, index) => (
                            <div
                              key={planWorkout.id || index}
                              className="flex items-center gap-2 p-2 bg-gray-50 rounded-lg"
                            >
                              <Dumbbell className="w-4 h-4 text-primary-600 flex-shrink-0" />
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium text-gray-900 truncate">
                                  {planWorkout.workout?.name || 'Workout'}
                                </p>
                                {planWorkout.workout?.estimatedDurationMinutes && (
                                  <p className="text-xs text-gray-500">
                                    {planWorkout.workout.estimatedDurationMinutes} min
                                  </p>
                                )}
                              </div>
                            </div>
                          ))}
                          {plan.workoutPlan.planWorkouts.length > 6 && (
                            <div className="flex items-center justify-center p-2 bg-gray-50 rounded-lg">
                              <span className="text-sm text-gray-600">
                                +{plan.workoutPlan.planWorkouts.length - 6} more
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="flex flex-col gap-2 sm:flex-shrink-0">
                    <Link
                      to="/member-dashboard"
                      className="inline-flex items-center justify-center px-4 py-2 bg-[var(--action-primary)] text-[var(--ink)] rounded-lg font-medium hover:bg-[var(--action-primary-pressed)] transition-colors text-sm"
                    >
                      <Play className="w-4 h-4 mr-2" />
                      View Calendar
                    </Link>
                    {plan.status === 'active' && (
                      <button className="inline-flex items-center justify-center px-4 py-2 bg-gray-100 text-gray-700 rounded-lg font-medium hover:bg-gray-200 transition-colors text-sm">
                        <TrendingUp className="w-4 h-4 mr-2" />
                        View Progress
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

